import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { registerEmail, loginEmail } from "@/lib/api";
import { maybeClaimReferral } from "@/lib/referral";

export const SignInModal = ({ open, onClose, title, subtitle }) => {
  useBodyScrollLock(open);
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const me = mode === "register" ? await registerEmail(email.trim(), password, name.trim()) : await loginEmail(email.trim(), password);
      await maybeClaimReferral(me?.is_new_user);
      window.location.assign("/app");
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-zinc-200 px-4 h-11 text-sm text-zinc-900 outline-none transition-colors focus:border-brand-orange";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 animate-in fade-in duration-200" data-testid="signin-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-2xl">
        <button onClick={onClose} data-testid="signin-modal-close" className="absolute right-4 top-4 text-zinc-400 transition-colors hover:text-black" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-col items-center text-center">
          <img src="/slip-logo.png" alt="Price Slip" className="h-16 w-16" />
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-zinc-900">{title || (mode === "register" ? "Create your account" : "Welcome back")}</h2>
          <p className="mt-2 text-sm text-zinc-500">{subtitle || "Sign in to start finding the cheapest prices."}</p>

          <div className="mt-6 w-full">
            <GoogleButton testId="modal-google-btn" className="w-full" />
          </div>

          <div className="my-5 flex w-full items-center gap-3">
            <div className="h-px flex-1 bg-zinc-200" />
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-400">or</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          <form onSubmit={submit} className="w-full space-y-3 text-left" data-testid="email-auth-form">
            {mode === "register" && (
              <input className={inputCls} type="text" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" />
            )}
            <input className={inputCls} type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input" />
            <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="auth-password-input" />
            {error && <p className="text-xs font-semibold text-red-600" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange py-3 text-sm font-bold uppercase tracking-[0.08em] text-black transition-transform hover:scale-[1.02] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === "register" ? "Create Account" : "Sign In"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => { setError(""); setMode(mode === "register" ? "login" : "register"); }}
            data-testid="auth-toggle-mode"
            className="mt-4 text-xs font-semibold text-zinc-500 transition-colors hover:text-brand-orange"
          >
            {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>

          <p className="mt-5 text-[11px] leading-relaxed text-zinc-400">
            By continuing you agree to keep it cheap. No spam, ever.
          </p>
        </div>
      </div>
    </div>
  );
};
